import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { UnifiedInboxService } from '../unified-inbox.service';
import { MsgOrigin } from '../unified-inbox.models';

interface AllMsgRow {
    msgId: number;
    conversationId: string;
    channelIcon: string;
    senderName: string;
    subject: string;
    preview: string;
    status: 'new' | 'replied' | 'archived';
    timestamp: string;
    isNew: boolean;
    isSelected: boolean;
}

@Component({
    selector: 'app-all-messages-list',
    templateUrl: './all-messages-list.component.html',
    styleUrls: ['./all-messages-list.component.scss'],
    standalone: false
})
export class AllMessagesListComponent implements OnInit, OnChanges {
    @Input() context: number;

    filter: 'all' | 'new' | 'replied' | 'archived' = 'all';

    rows: AllMsgRow[] = [];
    loading = false;
    loadError = '';

    // Pagination
    hasMore = false;
    loadingMore = false;
    private oldestDate: string | null = null;

    get filteredRows(): AllMsgRow[] {
        if (this.filter === 'all') { return this.rows; }
        return this.rows.filter(r => r.status === this.filter);
    }

    get newCount(): number { return this.rows.filter(r => r.status === 'new').length; }
    get repliedCount(): number { return this.rows.filter(r => r.status === 'replied').length; }
    get archivedCount(): number { return this.rows.filter(r => r.status === 'archived').length; }

    get allSelected(): boolean {
        const visible = this.filteredRows;
        return visible.length > 0 && visible.every(r => r.isSelected);
    }

    get someSelected(): boolean {
        return this.filteredRows.some(r => r.isSelected);
    }

    get selectedConversationIds(): string[] {
        return this.filteredRows.filter(r => r.isSelected).map(r => r.conversationId).filter(Boolean);
    }

    constructor(private unifiedInboxService: UnifiedInboxService) {}

    ngOnInit(): void {
        this.loadMessages();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['context'] && !changes['context'].firstChange) {
            this.rows = [];
            this.oldestDate = null;
            this.hasMore = false;
            this.loadMessages();
        }
    }

    loadMessages(): void {
        this.loading = true;
        this.loadError = '';
        this.unifiedInboxService.listConversations({ context: this.context, limit: 50 }).subscribe({
            next: res => {
                this.loading = false;
                const raw = res?.data?.data?.conversations || [];
                this.rows = this.mapToRows(raw);
                this.hasMore = res?.data?.data?.hasMore === true;
                if (this.rows.length > 0) {
                    this.oldestDate = raw[raw.length - 1]?.lastDate || null;
                }
            },
            error: err => {
                this.loading = false;
                this.loadError = err?.error?.message || 'Failed to load messages.';
            }
        });
    }

    loadMore(): void {
        if (this.loadingMore || !this.hasMore || !this.oldestDate) { return; }
        this.loadingMore = true;
        this.unifiedInboxService.listConversations({
            context: this.context,
            limit: 50,
            before: this.oldestDate
        }).subscribe({
            next: res => {
                this.loadingMore = false;
                const raw = res?.data?.data?.conversations || [];
                this.rows = [...this.rows, ...this.mapToRows(raw)];
                this.hasMore = res?.data?.data?.hasMore === true;
                if (raw.length > 0) {
                    this.oldestDate = raw[raw.length - 1]?.lastDate || null;
                }
            },
            error: () => { this.loadingMore = false; }
        });
    }

    private mapToRows(convList: any[]): AllMsgRow[] {
        return convList.map(c => {
            const origin: number = c.lastOrigin ?? 0;
            return {
                msgId: c.msgId || 0,
                conversationId: c.conversationId || '',
                channelIcon: this.originIcon(origin),
                senderName: c.lastSenderName || c.lastSenderEmail || 'Unknown',
                subject: this.cleanSubject(c.lastSubject || ''),
                preview: c.lastBodyPreview || '',
                status: (c.isRead === false ? 'new' : 'replied') as 'new' | 'replied' | 'archived',
                timestamp: this.formatDate(c.lastDate),
                isNew: c.isRead === false,
                isSelected: false
            };
        });
    }

    toggleSelection(row: AllMsgRow): void {
        row.isSelected = !row.isSelected;
    }

    selectAll(): void {
        const visible = this.filteredRows;
        const shouldSelect = !this.allSelected;
        visible.forEach(r => r.isSelected = shouldSelect);
    }

    archiveSelected(): void {
        const ids = this.selectedConversationIds;
        if (ids.length === 0) { return; }
        this.unifiedInboxService.bulkArchive(ids).subscribe({
            next: () => {
                this.rows = this.rows.filter(r => !ids.includes(r.conversationId));
            },
            error: () => { alert('Failed to archive selected messages.'); }
        });
    }

    statusPillClass(status: string): string {
        const map = { new: 'status-new', replied: 'status-replied', archived: 'status-archived' };
        return map[status] || '';
    }

    statusLabel(status: string): string {
        return status.charAt(0).toUpperCase() + status.slice(1);
    }

    private originIcon(origin: number): string {
        switch (origin) {
            case MsgOrigin.SMS: return '💬';
            case MsgOrigin.CALL: return '📞';
            case MsgOrigin.VRBO_API: return '🏠';
            case MsgOrigin.GUEST_PORTAL: return '🌺';
            case MsgOrigin.EMAIL: return '✉';
            case MsgOrigin.INTERNAL_NOTE: return '📝';
            case MsgOrigin.ADMIN_INBOX: return '✉';
            default: return '•';
        }
    }

    private cleanSubject(subject: string): string {
        if (!subject) { return ''; }
        if (/^email\/\d+\//.test(subject)) { return ''; }
        return subject.replace(/^(Re:\s*|Fwd:\s*)+/i, '').trim();
    }

    private formatDate(value: string): string {
        if (!value) { return ''; }
        const d = new Date(value);
        if (isNaN(d.getTime())) { return value; }
        const now = new Date();
        const sameDay = d.toDateString() === now.toDateString();
        if (sameDay) {
            return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        }
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
}
